import { useEffect, useMemo, useState } from 'react';

import { useInstanceId, useOpalUser } from '@frontend/hooks';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';
import { socketService } from '@frontend/services/socketService';
import { sendSentryError } from '@frontend/utils/sendSentryError';

import type { SocketContextValue } from './socketContext';
import { SocketContext } from './socketContext';

/**
 * Which reconnect attempt counts as "this outage is real". With the 30s delay
 * cap this is a couple of minutes in, well past a normal blip.
 */
const LONG_OUTAGE_ATTEMPT = 8;

type SocketProviderProps = {
  children: React.ReactNode;
};

// Desktop shell listens for this to drive its connection-lost bar; no-op in the browser.
function notifyDesktopSocketState(connected: boolean): void {
  document.dispatchEvent(new CustomEvent('opal::host-socket::state', { detail: { connected } }));
}

// Desktop shell routes its own relay socket by region but can't read the region
// from the URL, so hand it the active instance's hosted region. Always emit a
// region (defaulting to US) so switching back to a US instance corrects a relay
// left pointed at a previous EU instance. No-op in the browser (no listener).
function notifyDesktopInstanceRegion(instanceId: string, region: string): void {
  document.dispatchEvent(
    new CustomEvent('opal::instance-region', { detail: { instanceId, region } })
  );
}

export function SocketProvider({ children }: SocketProviderProps) {
  const [connected, setConnected] = useState(false);
  const { instanceId } = useInstanceId();
  const { opalUser } = useOpalUser();
  const hostedRegion = opalUser?.instance?.hosted_region;

  // Hand the desktop shell the active instance's region so its relay socket
  // routes to the same region this tab's socket does. Cheap DOM event; the
  // desktop shell reconnects the relay only if the region actually changed.
  useEffect(() => {
    if (instanceId) {
      notifyDesktopInstanceRegion(instanceId, hostedRegion || 'US');
    }
  }, [instanceId, hostedRegion]);

  useEffect(() => {
    let cancelled = false;
    let lastConnectError: Error | null = null;
    let handleConnect: (() => void) | null = null;
    let handleDisconnect: (() => void) | null = null;
    let handleConnectError: ((err: Error) => void) | null = null;
    let handleReconnectAttempt: ((...args: unknown[]) => void) | null = null;

    const initializeSocket = async () => {
      if (!instanceId) {
        console.warn('Cannot initialize socket: no instance ID available');
        return;
      }

      try {
        // Resolve once up front so an auth failure still surfaces here (and in
        // Sentry) rather than only as an opaque connect_error, but hand the
        // socket the *provider* — see socketService.init. A captured token
        // string goes stale and every later reconnect replays a dead one.
        await getOrRenewOptiIdToken();

        // Check if effect was cleaned up while waiting for token
        if (cancelled) {
          return;
        }

        socketService.init(getOrRenewOptiIdToken, instanceId, hostedRegion);

        // Define handlers as named functions so we can remove them specifically
        handleConnect = () => {
          console.warn('Socket connected to server');
          lastConnectError = null;
          setConnected(true);
          notifyDesktopSocketState(true);
        };

        handleDisconnect = () => {
          console.warn('Socket disconnected from server');
          setConnected(false);
          notifyDesktopSocketState(false);
        };

        handleConnectError = (err: Error) => {
          console.error('Socket connection error:', err);
          if (err instanceof Error) {
            lastConnectError = err;
          }
          setConnected(false);
          notifyDesktopSocketState(false);
        };

        // `reconnect_failed` can no longer fire — reconnectionAttempts is
        // Infinity — so reporting from there would just go quiet. Report from
        // the attempt counter instead: once per outage, at the point where
        // retrying has gone on long enough to be a real problem rather than a
        // blip. This matters more than it looks, because a long outage is
        // invisible in the UI: React Query silently falls back to polling.
        handleReconnectAttempt = (attemptNumber: unknown) => {
          console.warn(`Socket reconnection attempt ${attemptNumber}`);
          if (attemptNumber !== LONG_OUTAGE_ATTEMPT) {
            return;
          }
          sendSentryError(lastConnectError ?? new Error('Socket reconnecting for a while'), {
            attempt: attemptNumber,
            online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
            transport: socketService.getSocket()?.io?.engine?.transport?.name,
          });
        };

        // Register socket event handlers
        socketService.on('connect', handleConnect);
        socketService.on('disconnect', handleDisconnect);
        socketService.on('connect_error', handleConnectError);

        // Register Manager event handlers (for reconnection events)
        socketService.onManager('reconnect_attempt', handleReconnectAttempt);

        // Connect
        socketService.connect();
      } catch (error) {
        console.error('Failed to initialize socket with authentication:', error);
        sendSentryError(error instanceof Error ? error : new Error('Socket auth init failed'), {
          context: 'SocketProvider.initializeSocket',
          operation: 'token_attach',
          instanceId,
        });
      }
    };

    initializeSocket();

    // Synchronous cleanup - runs immediately when effect unmounts
    return () => {
      cancelled = true;

      // Remove socket event handlers if they were registered
      if (handleConnect) socketService.off('connect', handleConnect);
      if (handleDisconnect) socketService.off('disconnect', handleDisconnect);
      if (handleConnectError) socketService.off('connect_error', handleConnectError);

      // Remove Manager event handlers if they were registered
      if (handleReconnectAttempt)
        socketService.offManager('reconnect_attempt', handleReconnectAttempt);

      // Only disconnect if socket was actually initialized
      if (socketService.getSocket()) {
        socketService.disconnect();
      }

      setConnected(false);
    };
  }, [instanceId, hostedRegion]);

  const value = useMemo<SocketContextValue>(() => {
    const on = <T,>(event: string, callback: (data: T) => void) => {
      socketService.on(event, callback);
    };

    const off = <T,>(event: string, callback?: (data: T) => void) => {
      socketService.off(event, callback);
    };

    const emit = (event: string, data?: unknown) => {
      socketService.emit(event, data);
    };

    return {
      connected,
      on,
      off,
      emit,
    };
  }, [connected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
